import { useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import {
  useCreateRecipe,
  useUpdateRecipe,
  useAddRecipeMaterial,
  useRecipe,
  type RecipeListDTO,
} from '../../hooks/useRecipes';
import { useMaterials, type MaterialDTO } from '../../hooks/useMaterials';
import { CATEGORIA_OPTIONS, generateItemCode } from '../../lib/item-utils';
import { toast } from '../ui/Toast';

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm2' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'unid' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'glb' },
];

interface CreateRecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (recipe: RecipeListDTO) => void;
  skipMaterials?: boolean;
}

export function CreateRecipeModal({ isOpen, onClose, onCreated, skipMaterials }: CreateRecipeModalProps) {
  const createRecipe = useCreateRecipe();
  const updateRecipe = useUpdateRecipe();

  const [step, setStep] = useState<1 | 2>(1);
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('gypsum');
  const [unidad, setUnidad] = useState('m2');

  // Step 2 editable prices
  const [precioMo, setPrecioMo] = useState('');
  const [utilidad, setUtilidad] = useState('');

  const [createdRecipeId, setCreatedRecipeId] = useState<string | null>(null);
  const [createdRecipeDTO, setCreatedRecipeDTO] = useState<RecipeListDTO | null>(null);

  const codigo = generateItemCode(categoria, nombre);

  const resetAll = () => {
    setStep(1);
    setNombre('');
    setCategoria('gypsum');
    setUnidad('m2');
    setPrecioMo('');
    setUtilidad('');
    setCreatedRecipeId(null);
    setCreatedRecipeDTO(null);
  };

  const handleCreateRecipe = async () => {
    if (!nombre.trim()) {
      toast('error', 'El nombre es requerido');
      return;
    }

    try {
      const result = await createRecipe.mutateAsync({
        codigo,
        nombre: nombre.trim().toUpperCase(),
        categoria: categoria as 'gypsum' | 'pintura' | 'empaste' | 'impermeabilizacion' | 'general',
        unidad: unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb',
        precioMoBase: 0,
        precioMaterialesBase: 0,
        porcentajeUtilidadBase: 0,
      });

      const dto: RecipeListDTO = {
        id: result.id,
        codigo: result.codigo,
        nombre: result.nombre,
        categoria: result.categoria,
        unidad: result.unidad,
        precio_mo_base: result.precio_mo_base,
        precio_materiales_base: result.precio_materiales_base,
        porcentaje_utilidad_base: result.porcentaje_utilidad_base,
        is_active: true,
        materials_count: 0,
        created_at: result.created_at,
        updated_at: result.updated_at,
      };

      if (skipMaterials) {
        if (onCreated) onCreated(dto);
        resetAll();
        onClose();
        return;
      }

      setCreatedRecipeId(result.id);
      setCreatedRecipeDTO(dto);
      setStep(2);
    } catch {
      // hook handles error toast
    }
  };

  const handleFinish = async () => {
    if (!createdRecipeId || !createdRecipeDTO) return;

    const mo = parseFloat(precioMo) || 0;
    const util = parseFloat(utilidad) || 0;

    if (mo !== 0 || util !== 0) {
      try {
        await updateRecipe.mutateAsync({
          id: createdRecipeId,
          data: { precioMoBase: mo, porcentajeUtilidadBase: util },
        });
      } catch {
        // non-blocking — prices can be edited later
      }
    }

    if (onCreated) {
      onCreated({
        ...createdRecipeDTO,
        precio_mo_base: mo,
        porcentaje_utilidad_base: util,
      });
    }
    resetAll();
    onClose();
  };

  const handleClose = () => {
    if (step === 2 && onCreated && createdRecipeDTO) {
      onCreated(createdRecipeDTO);
    }
    resetAll();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 1 ? 'Crear Nuevo APU' : `Agregar Materiales — ${createdRecipeDTO?.nombre ?? ''}`}
      size="lg"
    >
      {step === 1 ? (
        <Step1CreateRecipe
          codigo={codigo}
          nombre={nombre}
          setNombre={setNombre}
          categoria={categoria}
          setCategoria={setCategoria}
          unidad={unidad}
          setUnidad={setUnidad}
          onSubmit={handleCreateRecipe}
          onCancel={handleClose}
          isPending={createRecipe.isPending}
        />
      ) : (
        <Step2AddMaterials
          recipeId={createdRecipeId!}
          precioMo={precioMo}
          setPrecioMo={setPrecioMo}
          utilidad={utilidad}
          setUtilidad={setUtilidad}
          onFinish={handleFinish}
          isFinishing={updateRecipe.isPending}
        />
      )}
    </Modal>
  );
}

// ── Step 1: Solo info básica ─────────────────────────────────────────
function Step1CreateRecipe({
  codigo, nombre, setNombre, categoria, setCategoria,
  unidad, setUnidad, onSubmit, onCancel, isPending,
}: {
  codigo: string;
  nombre: string; setNombre: (v: string) => void;
  categoria: string; setCategoria: (v: string) => void;
  unidad: string; setUnidad: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Código (auto-generado)</label>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-500">
          {codigo || '---'}
        </div>
      </div>

      <Input
        label="Nombre"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder="Ej: TUMBADO REGULAR"
      />

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Categoría"
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          options={CATEGORIA_OPTIONS}
        />
        <Select
          label="Unidad"
          value={unidad}
          onChange={(e) => setUnidad(e.target.value)}
          options={UNIDAD_OPTIONS}
        />
      </div>

      <p className="text-xs text-gray-400">
        Los precios se configuran en el siguiente paso, una vez agregados los materiales.
      </p>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button variant="primary" onClick={onSubmit} isLoading={isPending} loadingText="Creando...">
          Siguiente — Agregar materiales
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: Materiales + MO + Utilidad ──────────────────────────────
function Step2AddMaterials({
  recipeId,
  precioMo, setPrecioMo,
  utilidad, setUtilidad,
  onFinish, isFinishing,
}: {
  recipeId: string;
  precioMo: string; setPrecioMo: (v: string) => void;
  utilidad: string; setUtilidad: (v: string) => void;
  onFinish: () => void;
  isFinishing: boolean;
}) {
  const { data: recipeDetail } = useRecipe(recipeId);
  const addMaterial = useAddRecipeMaterial();

  // Separate state for each section
  const [matSearch, setMatSearch] = useState('');
  const [selectedMat, setSelectedMat] = useState<MaterialDTO | null>(null);
  const [cantidadMat, setCantidadMat] = useState('');

  const [mdoSearch, setMdoSearch] = useState('');
  const [selectedMdo, setSelectedMdo] = useState<MaterialDTO | null>(null);
  const [cantidadMdo, setCantidadMdo] = useState('');

  const { data: matsData } = useMaterials({ search: matSearch || undefined, limit: 30 });
  // Filter out MDO items client-side so regular materials list is clean
  const regularMaterials = (matsData?.data ?? []).filter((m) => m.categoria !== 'mdo');

  const { data: mdoData } = useMaterials({ search: mdoSearch || undefined, categoria: 'mdo', limit: 30 });
  const mdoMaterials = mdoData?.data ?? [];

  const recipeMaterials = recipeDetail?.materials ?? [];
  const precioMaterialesBase = recipeDetail?.precio_materiales_base ?? 0;

  const mo = parseFloat(precioMo) || 0;
  const util = parseFloat(utilidad) || 0;
  const subtotal = precioMaterialesBase + mo;
  const total = subtotal * (1 + util / 100);

  const handleAdd = async (material: MaterialDTO, cantidad: string, clearFn: () => void) => {
    const qty = parseFloat(cantidad);
    if (!qty || qty <= 0) {
      toast('error', 'La cantidad debe ser mayor a 0');
      return;
    }
    try {
      await addMaterial.mutateAsync({
        recipeId,
        data: { materialId: material.id, cantidadPorUnidad: qty },
      });
      clearFn();
    } catch {
      // hook handles toast
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Combined added items table ── */}
      {recipeMaterials.length > 0 && (
        <div className="rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500">
                <th className="px-3 py-2">Ítem</th>
                <th className="px-3 py-2 text-right">Cant/Und</th>
                <th className="px-3 py-2 text-right">P. Unit.</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {recipeMaterials.map((m) => (
                <tr key={m.id} className="border-t border-gray-50">
                  <td className="px-3 py-2 text-gray-700">{m.material_nombre}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{m.cantidad_por_unidad}</td>
                  <td className="px-3 py-2 text-right text-gray-600">${m.material_precio_unitario.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-800">
                    ${(m.material_precio_unitario * m.cantidad_por_unidad).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Agregar Materiales ── */}
      <AddItemSection
        title="Agregar Material"
        search={matSearch}
        setSearch={setMatSearch}
        selected={selectedMat}
        setSelected={setSelectedMat}
        cantidad={cantidadMat}
        setCantidad={setCantidadMat}
        suggestions={regularMaterials}
        onAdd={() =>
          handleAdd(selectedMat!, cantidadMat, () => {
            setSelectedMat(null);
            setCantidadMat('');
            setMatSearch('');
          })
        }
        isPending={addMaterial.isPending}
        placeholder="Buscar material por nombre..."
        accentColor="blue"
      />

      {/* ── Agregar Mano de Obra ── */}
      <AddItemSection
        title="Agregar Mano de Obra"
        search={mdoSearch}
        setSearch={setMdoSearch}
        selected={selectedMdo}
        setSelected={setSelectedMdo}
        cantidad={cantidadMdo}
        setCantidad={setCantidadMdo}
        suggestions={mdoMaterials}
        onAdd={() =>
          handleAdd(selectedMdo!, cantidadMdo, () => {
            setSelectedMdo(null);
            setCantidadMdo('');
            setMdoSearch('');
          })
        }
        isPending={addMaterial.isPending}
        placeholder="Buscar ítem de mano de obra..."
        accentColor="amber"
      />

      {/* MO + Utilidad editable */}
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Precios base</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">Mat. (auto-calculado)</label>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
              ${precioMaterialesBase.toFixed(2)}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Mano de Obra ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={precioMo}
              onChange={(e) => setPrecioMo(e.target.value)}
              placeholder="0.00"
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">Utilidad (%)</label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={utilidad}
              onChange={(e) => setUtilidad(e.target.value)}
              placeholder="0"
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            />
          </div>
        </div>

        {/* Live total */}
        <div className="flex items-center justify-between pt-1 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            (Mat ${precioMaterialesBase.toFixed(2)} + MO ${mo.toFixed(2)}) × (1 + {util}%)
          </span>
          <span className="text-sm font-bold text-brand-blue">${total.toFixed(2)} / und</span>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="primary"
          onClick={onFinish}
          isLoading={isFinishing}
          loadingText="Guardando..."
        >
          {recipeMaterials.length > 0 ? 'Finalizar y agregar a proforma' : 'Finalizar sin materiales'}
        </Button>
      </div>
    </div>
  );
}

// ── Shared search + add section ──────────────────────────────────────
function AddItemSection({
  title, search, setSearch, selected, setSelected,
  cantidad, setCantidad, suggestions, onAdd, isPending, placeholder, accentColor,
}: {
  title: string;
  search: string;
  setSearch: (v: string) => void;
  selected: MaterialDTO | null;
  setSelected: (m: MaterialDTO | null) => void;
  cantidad: string;
  setCantidad: (v: string) => void;
  suggestions: MaterialDTO[];
  onAdd: () => void;
  isPending: boolean;
  placeholder: string;
  accentColor: 'blue' | 'amber';
}) {
  const borderClass = accentColor === 'amber' ? 'border-amber-100 bg-amber-50/30' : 'border-blue-100 bg-blue-50/30';

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</p>

      <div className={`rounded-lg border p-3 space-y-3 ${borderClass}`}>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={selected ? selected.nombre : search}
            onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
            placeholder={placeholder}
            className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          />
        </div>

        {search && !selected && suggestions.length > 0 && (
          <div className="max-h-36 overflow-y-auto rounded-md border border-gray-200 bg-white">
            {suggestions.map((mat) => (
              <button
                key={mat.id}
                onClick={() => { setSelected(mat); setSearch(''); }}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="text-gray-700">{mat.nombre}</span>
                <span className="text-xs text-gray-400">${mat.precio_unitario.toFixed(2)} / {mat.unidad}</span>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-gray-500">Cantidad por unidad de APU</label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={onAdd}
              isLoading={isPending}
              leftIcon={<Plus size={14} />}
            >
              Agregar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
